import { useNavigate } from "react-router-dom";
import { Anchor, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MARINE_CATEGORIES, marineCategoryHref } from "@/lib/marineCategories";

interface PartsHierarchyMenuProps {
  onClose?: () => void;
  onInteraction?: () => void;
}

/**
 * Marine FSC category dropdown. The legacy `/dwapi/ecommerce/groups` tree on
 * this hosted DW instance is a locked Keystone RV PIM hierarchy, so we derive
 * navigation client-side from the FSC tags carried on every marine part.
 */
export function PartsHierarchyMenu({ onClose, onInteraction }: PartsHierarchyMenuProps) {
  const navigate = useNavigate();

  const go = (path: string) => {
    onInteraction?.();
    navigate(path);
    onClose?.();
  };

  return (
    <div className="w-80">
      <div className="px-4 py-3 border-b border-border/50">
        <span className="text-sm font-semibold">Shop Marine Parts</span>
      </div>
      <ScrollArea style={{ height: "420px" }}>
        <div className="p-2 space-y-1">
          {MARINE_CATEGORIES.map((cat) => (
            <button
              key={cat.code}
              onClick={() => go(marineCategoryHref(cat.code))}
              className={cn(
                "w-full flex items-center justify-between gap-2 rounded-lg p-3 text-sm transition-all duration-200",
                "hover:bg-accent hover:text-accent-foreground group text-left"
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Anchor className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                <div className="min-w-0">
                  <div className="font-medium truncate">{cat.name}</div>
                  <div className="text-xs text-muted-foreground">FSC {cat.code}</div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform group-hover:translate-x-0.5" />
            </button>
          ))}
        </div>
      </ScrollArea>
      <div className="border-t border-border/50">
        <button
          onClick={() => go("/parts")}
          className="w-full px-4 py-3 text-sm font-medium text-primary hover:bg-accent transition-colors text-left"
        >
          View All Marine Parts →
        </button>
      </div>
    </div>
  );
}